import React, { useEffect, useMemo, useRef, useState } from "react";
import Cookies from "js-cookie";
import {
  Box,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
  useMediaQuery,
  TextField,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Badge,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import ChatIcon from "@mui/icons-material/Chat";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import DownloadIcon from "@mui/icons-material/Download";
import CancelIcon from "@mui/icons-material/Cancel";
import DescriptionIconMui from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DescriptionIcon from "@mui/icons-material/Description";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import {
  AccessTime,
  Done,
  DoneAll,
  ErrorOutline,
  PersonAddOutlined,
  EditOutlined,
  CheckOutlined,
  Close,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import useNotification from "../common/Notification";
import TemplateMessageBubble from "./testMessage/TemplateMessageBubble";

const MessageStatusIcon = ({ status }) => {
  if (!status) return null;

  const statusUpper = status.toUpperCase();

  switch (statusUpper) {
    case "PENDING":
      return (
        <AccessTime
          sx={{
            fontSize: 14,
            opacity: 0.7,
            ml: 0.5,
          }}
        />
      );
    case "SENT":
      return (
        <Done
          sx={{
            fontSize: 14,
            opacity: 0.7,
            ml: 0.5,
          }}
        />
      );
    case "DELIVERED":
      return (
        <DoneAll
          sx={{
            fontSize: 14,
            opacity: 0.7,
            ml: 0.5,
            color: "rgba(0, 0, 0, 0.6)",
          }}
        />
      );
    case "READ":
      return (
        <DoneAll
          sx={{
            fontSize: 14,
            opacity: 1,
            ml: 0.5,
            color: "#4fc3f7",
          }}
        />
      );
    case "FAILED":
      return (
        <ErrorOutline
          sx={{
            fontSize: 14,
            opacity: 0.8,
            ml: 0.5,
            color: "#e53935",
          }}
        />
      );
    default:
      return null;
  }
};

const ContactChatWindow = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { phoneNumber } = useParams();
  const notify = useNotification();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [messageInput, setMessageInput] = useState("");
  const [templateDetailsCache, setTemplateDetailsCache] = useState({});
  const [loadingTemplates, setLoadingTemplates] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState("");
  const [mediaType, setMediaType] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [sendingMedia, setSendingMedia] = useState(false);
  const [botEnabled, setBotEnabled]       = useState(null);
  const [botToggling, setBotToggling]     = useState(false);
  const [crmExists, setCrmExists]         = useState(false);
  const [crmName, setCrmName]             = useState("");
  const [addCrmOpen, setAddCrmOpen]       = useState(false);
  const [editNameOpen, setEditNameOpen]   = useState(false);
  const [crmNameInput, setCrmNameInput]   = useState("");
  const [savingCrm, setSavingCrm]         = useState(false);
  const [crmContact, setCrmContact]       = useState(null); // full contact object for pre-fill
  const SOURCE_OPTIONS = ["Facebook", "Google", "Manual", "WhatsApp", "Referral"];
  const EMPTY_CRM_FORM = { name: "", email: "", company_name: "", source: "", status: "interested", is_bot_enabled: true };
  const [crmForm, setCrmForm]             = useState(EMPTY_CRM_FORM);
  const messagesListRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const fileInputRef = useRef(null);

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    return phone.replace(/\D/g, "");
  };

  const formattedRecipient = formatPhoneNumber(phoneNumber);
  const roles = useMemo(
    () => (Cookies.get("userRole") || "").split(",").map((r) => r.trim()),
    []
  );
  const isWbAgent = roles.includes("WB-AGENT");
  const hasCrmRole = roles.includes("CRM-ADMIN") || roles.includes("CRM-USER");

  // ── Fetch CRM contact info (bot status + name + existence) ─────────────────
  const fetchCrmContact = () => {
    if (!formattedRecipient) return;
    axiosInstance
      .get(`${base_url}/crm/contacts`, { params: { search: formattedRecipient, per_page: 5 } })
      .then((res) => {
        const paginator = res.data?.data ?? {};
        const list = Array.isArray(paginator.data) ? paginator.data : [];
        const contact = list.find((c) => String(c.phone) === String(formattedRecipient));
        if (contact) {
          setBotEnabled(Boolean(contact.is_bot_enabled));
          setCrmExists(true);
          setCrmName(contact.name || "");
          setCrmContact(contact);
        } else {
          setBotEnabled(true);
          setCrmExists(false);
          setCrmName("");
          setCrmContact(null);
        }
      })
      .catch(() => { setBotEnabled(true); setCrmExists(false); });
  };

  useEffect(() => { fetchCrmContact(); }, [formattedRecipient]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleBot = async () => {
    const newVal = !botEnabled;
    setBotToggling(true);
    try {
      await axiosInstance.post(`${base_url}/crm/contacts/bot-enabled`, {
        phone: formattedRecipient,
        is_bot_enabled: newVal,
      });
      setBotEnabled(newVal);
      notify(`Auto bot reply ${newVal ? "enabled" : "disabled"} for ${displayPhone}`, "success");
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to update bot setting", "error");
    } finally {
      setBotToggling(false);
    }
  };
  const handleAddToCrm = async () => {
    setSavingCrm(true);
    try {
      await axiosInstance.post(`${base_url}/crm/contacts/move-to-crm`, {
        phone: formattedRecipient,
        ...(crmForm.name.trim()         ? { name: crmForm.name.trim() }               : {}),
        ...(crmForm.email.trim()        ? { email: crmForm.email.trim() }              : {}),
        ...(crmForm.company_name.trim() ? { company_name: crmForm.company_name.trim() }: {}),
        ...(crmForm.source.trim()       ? { source: crmForm.source.trim() }            : {}),
        status:         crmForm.status,
        is_bot_enabled: crmForm.is_bot_enabled,
      });
      notify(`Contact ${crmExists ? "updated" : "added to CRM"} successfully`, "success");
      setAddCrmOpen(false);
      setCrmForm(EMPTY_CRM_FORM);
      fetchCrmContact();
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to save contact", "error");
    } finally {
      setSavingCrm(false);
    }
  };

  const handleEditName = async () => {
    if (!crmNameInput.trim()) return;
    setSavingCrm(true);
    try {
      await axiosInstance.post(`${base_url}/crm/contacts/edit-name`, {
        phone: formattedRecipient,
        name: crmNameInput.trim(),
      });
      notify("Contact name updated successfully", "success");
      setEditNameOpen(false);
      setCrmName(crmNameInput.trim());
      setCrmNameInput("");
      fetchCrmContact();
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to update name", "error");
    } finally {
      setSavingCrm(false);
    }
  };
  // ──────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!formattedRecipient) return;

    let cancelled = false;

    const fetchConversation = async () => {
      setLoading(true);

      const fetchTemplateDetails = async (templateId) => {
        if (!templateId) return null;

        if (templateDetailsCache[templateId]) {
          return templateDetailsCache[templateId];
        }

        if (loadingTemplates[templateId]) {
          return null;
        }

        setLoadingTemplates((prev) => ({ ...prev, [templateId]: true }));

        try {
          const response = await axiosInstance.get(
            `${base_url}/whatsapp-business/template/${templateId}/details`,
            {
              params: {
                fields:
                  "id,name,language,category,status,components,previous_category,parameter_format",
              },
              headers: {
                Accept: "application/json",
              },
            }
          );

          if (response.data && response.data.id) {
            const templateData = response.data;
            setTemplateDetailsCache((prev) => ({
              ...prev,
              [templateId]: templateData,
            }));
            return templateData;
          }
        } catch (error) {
          console.error(
            `Error fetching template details for ${templateId}:`,
            error
          );
        } finally {
          setLoadingTemplates((prev) => {
            const newState = { ...prev };
            delete newState[templateId];
            return newState;
          });
        }

        return null;
      };

      try {
        const res = await axiosInstance.get(
          `${base_url}/whatsapp-business/chat/messages/conversation`,
          {
            params: {
              recipient: formattedRecipient,
              page: 1,
              per_page: 50,
            },
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (!res.data?.success) {
          if (!cancelled) {
            setMessages([]);
            notify(res.data?.message || "Failed to load conversation", "error");
          }
          return;
        }

        const { incoming = [], outgoing = [] } = res.data.data || {};

        const processMessage = (msg, senderType) => {
          const hasTemplateId = !!msg.template_id;

          let parsedPayload = null;
          if (msg.payload) {
            try {
              parsedPayload =
                typeof msg.payload === "string"
                  ? JSON.parse(msg.payload)
                  : msg.payload;
            } catch (e) {
              console.error("Error parsing payload:", e);
              parsedPayload = null;
            }
          }

          const messageObj = {
            id: msg.id,
            text: msg.message_content || "",
            sender: senderType,
            timestamp: new Date(msg.created_at).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
            }),
            created_at: msg.created_at,
            message_id: msg.message_id,
            status: msg.status,
            media_link: msg.media_link || null,
            media_type: msg.media_type || null,
            is_bot_reply:
              senderType === "bot" ? msg.is_bot_reply === true : false,
            sender_name: senderType === "bot" ? msg.sender_name || null : null,
          };

          if (hasTemplateId) {
            messageObj.is_template = true;
            messageObj.template_id = msg.template_id;
            messageObj.payload = parsedPayload;
          } else {
            messageObj.is_template = false;
            messageObj.template_id = null;
            messageObj.payload = null;
          }

          return messageObj;
        };

        const allMessages = [
          ...incoming.map((m) => processMessage(m, "user")),
          ...outgoing.map((m) => processMessage(m, "bot")),
        ];

        const templateIds = new Set();
        allMessages.forEach((msg) => {
          if (msg.template_id) {
            templateIds.add(msg.template_id);
          }
        });
        const templatePromises = Array.from(templateIds).map((templateId) =>
          fetchTemplateDetails(templateId)
        );
        await Promise.all(templatePromises);

        allMessages.sort((a, b) => {
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return dateA - dateB;
        });

        if (!cancelled) {
          setMessages(allMessages);
        }
      } catch (e) {
        if (!cancelled) {
          notify(
            e.response?.data?.message || "Failed to load conversation",
            "error"
          );
          setMessages([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchConversation();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedRecipient]);

  const messagesWithDateHeaders = useMemo(() => {
    if (!messages || messages.length === 0) return [];

    const result = [];
    let lastDateLabel = null;

    messages.forEach((msg) => {
      const createdAt = msg.created_at || msg.timestamp_raw || null;
      let dateLabel = null;
      if (createdAt) {
        const d = new Date(createdAt);
        if (!Number.isNaN(d.getTime())) {
          const day = d.getDate();
          const month = d.toLocaleString("en-US", { month: "long" });
          const year = d.getFullYear();
          dateLabel = `${day} ${month} ${year}`;
        }
      }

      if (dateLabel && dateLabel !== lastDateLabel) {
        result.push({
          __type: "date-header",
          id: `date-${dateLabel}-${msg.id}`,
          label: dateLabel,
        });
        lastDateLabel = dateLabel;
      }

      result.push(msg);
    });

    return result;
  }, [messages]);

  const handleSend = async () => {
    const text = messageInput.trim();
    if (!text || !formattedRecipient) return;

    try {
      setSending(true);
      await axiosInstance.post(
        `${base_url}/whatsapp-business/chat/messages/send`,
        {
          to: formattedRecipient,
          message: text,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      setMessageInput("");
    } catch (e) {
      notify(
        e.response?.data?.message || "Failed to send message",
        "error"
      );
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !sending && !uploadingMedia && !sendingMedia) {
      e.preventDefault();
      if (mediaFile) {
        handleMediaUpload();
      } else {
        handleSend();
      }
    }
  };

  const professionalEmojis = [
    "🙂",
    "😊",
    "😄",
    "😃",
    "😁",
    "🙏",
    "👍",
    "👎",
    "👌",
    "✌️",
    "🤝",
    "👏",
    "🙌",
    "💪",
    "✅",
    "❌",
    "✔️",
    "⭐",
    "🌟",
    "💯",
  ];

  const handleEmojiClick = (emoji) => {
    setMessageInput((prev) => prev + emoji);
    setShowEmojiPicker(false);
  };

  const handleMessagesScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    if (isNearBottom && messages.length) {
      // keep scrolled to bottom when near
    }
  };

  const scrollToBottom = () => {
    if (messagesListRef.current) {
      messagesListRef.current.scrollTop =
        messagesListRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (messages.length) {
      scrollToBottom();
    }
  }, [messages]);

  const handleMediaFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileType = file.type.toLowerCase();
    let detectedType = null;
    if (fileType.startsWith("image/")) detectedType = "image";
    else if (fileType.startsWith("video/")) detectedType = "video";
    else if (fileType.startsWith("audio/")) detectedType = "audio";
    else detectedType = "document";

    setMediaFile(file);
    setMediaType(detectedType);

    if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setMediaPreviewUrl("");
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleMediaUpload = async () => {
    if (!mediaFile || !formattedRecipient) {
      notify("Please select a file", "error");
      return;
    }

    setUploadingMedia(true);
    try {
      const formData = new FormData();
      formData.append("file", mediaFile);
      formData.append("type", mediaType);

      const response = await axiosInstance.post(
        `${base_url}/whatsapp-business/media/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const mediaLinkFromResponse = response.data?.cloudflare_path || null;
      const mediaTypeFromResponse = response.data?.type || null;

      if (mediaLinkFromResponse) {
        await handleSendMediaMessage(
          mediaLinkFromResponse,
          mediaTypeFromResponse
        );
      } else {
        notify("Failed to get media URL from upload response", "error");
      }
    } catch (error) {
      console.error("Error uploading media:", error);
      notify(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to upload media",
        "error"
      );
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleSendMediaMessage = async (mediaLink, mediaTypeValue) => {
    if (!formattedRecipient || !mediaLink) {
      notify("Contact or media link not found", "error");
      return;
    }

    setSendingMedia(true);
    try {
      let apiMediaType = "IMAGE";
      if (mediaTypeValue === "video" || mediaType === "video")
        apiMediaType = "VIDEO";
      else if (mediaTypeValue === "audio" || mediaType === "audio")
        apiMediaType = "AUDIO";
      else if (
        mediaTypeValue === "document" ||
        mediaType === "document"
      )
        apiMediaType = "DOCUMENT";

      const response = await axiosInstance.post(
        `${base_url}/whatsapp-business/media-message/send`,
        {
          media_link: mediaLink,
          media_type: apiMediaType.toLowerCase(),
          recipient: formattedRecipient,
          caption: messageInput.trim() || undefined,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (response.data && response.data.success) {
        notify("Media message sent successfully", "success");
        setMediaFile(null);
        setMediaPreviewUrl("");
        setMediaType(null);
        setMessageInput("");
        scrollToBottom();
      } else {
        throw new Error("Failed to send media message");
      }
    } catch (error) {
      console.error("Error sending media message:", error);
      notify(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to send media message",
        "error"
      );
    } finally {
      setSendingMedia(false);
    }
  };

  const handleCancelMedia = () => {
    setMediaFile(null);
    setMediaPreviewUrl("");
    setMediaType(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const displayPhone = phoneNumber || formattedRecipient || "Unknown";

  return (
    <>
    <Box
      sx={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          backgroundColor: WB_UI.color.softInfoBg,
        }}
      >
        <IconButton
          edge="start"
          onClick={() => {
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              window.close();
            }
          }}
          sx={{ mr: 1 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              sx={{ fontWeight: 700, lineHeight: 1.2 }}
            >
              {crmName ? crmName : displayPhone}
            </Typography>
            {crmName && (
              <Typography
                variant="caption"
                sx={{
                  color: "#6b7280",
                  backgroundColor: WB_UI.color.softInfoBg,
                  border: `1px solid ${WB_UI.color.border}`,
                  borderRadius: 1,
                  px: 0.75,
                  py: 0.2,
                  fontWeight: 500,
                  fontSize: "0.7rem",
                }}
              >
                {displayPhone}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {crmExists
              ? "CRM contact · Showing recent messages"
              : "Showing recent messages for this number"}
          </Typography>
        </Box>

        {/* CRM action buttons — CRM-ADMIN or CRM-USER */}
        {hasCrmRole && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 1.5 }}>
          {/* Add / View CRM */}
          <Tooltip title={crmExists ? `In CRM${crmName ? ` · ${crmName}` : ""}` : "Add to CRM"} arrow>
            <Box>
              <Button
                size="small"
                variant={crmExists ? "outlined" : "contained"}
                startIcon={crmExists ? <CheckOutlined sx={{ fontSize: 14 }} /> : <PersonAddOutlined sx={{ fontSize: 14 }} />}
                onClick={() => {
                  setCrmForm(crmContact ? {
                    name:         crmContact.name         || "",
                    email:        crmContact.email        || "",
                    company_name: crmContact.company_name || "",
                    source:       crmContact.source       || "",
                    status:       crmContact.status       || "interested",
                    is_bot_enabled: Boolean(crmContact.is_bot_enabled),
                  } : EMPTY_CRM_FORM);
                  setAddCrmOpen(true);
                }}
                sx={{
                  fontSize: "0.72rem",
                  px: 1.25,
                  py: 0.4,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: "unset",
                  ...(crmExists
                    ? { borderColor: "#bbf7d0", color: "#166534", backgroundColor: "#f0fdf4", "&:hover": { backgroundColor: "#dcfce7", borderColor: "#86efac" } }
                    : { backgroundColor: "#1a2333", "&:hover": { backgroundColor: "#374151" } }
                  ),
                }}
              >
                {crmExists ? "In CRM" : "Add to CRM"}
              </Button>
            </Box>
          </Tooltip>

          {/* Edit Name — only if contact exists in CRM */}
          {crmExists && (
            <Tooltip title={`Edit name${crmName ? ` · ${crmName}` : ""}`} arrow>
              <IconButton
                size="small"
                onClick={() => { setCrmNameInput(crmName); setEditNameOpen(true); }}
                sx={{
                  width: 30, height: 30,
                  border: "1px solid #e5e7eb",
                  borderRadius: 1.5,
                  color: "#6b7280",
                  "&:hover": { backgroundColor: "#f3f4f6", color: "#111827" },
                }}
              >
                <EditOutlined sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        )}

        {/* Bot toggle — WB-AGENT only */}
        {isWbAgent && <Tooltip
          title={
            botEnabled === null
              ? "Loading bot status…"
              : botEnabled
              ? "Auto bot reply is ON — click to disable"
              : "Auto bot reply is OFF — click to enable"
          }
          arrow
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              border: "1px solid",
              borderColor: botEnabled ? "#ddd6fe" : "#e5e7eb",
              backgroundColor: botEnabled ? "#f5f3ff" : "#f9fafb",
              cursor: botEnabled === null ? "default" : "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: botEnabled ? "#ede9fe" : "#f3f4f6",
              },
            }}
            onClick={botEnabled === null || botToggling ? undefined : toggleBot}
          >
            {botToggling ? (
              <CircularProgress size={14} sx={{ color: "#7c3aed" }} />
            ) : (
              <SmartToyIcon
                sx={{
                  fontSize: 16,
                  color: botEnabled ? "#7c3aed" : "#9ca3af",
                  transition: "color 0.2s ease",
                }}
              />
            )}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: botEnabled ? "#7c3aed" : "#6b7280",
                fontSize: "0.7rem",
                display: { xs: "none", sm: "block" },
              }}
            >
              Bot
            </Typography>
            <Switch
              size="small"
              checked={Boolean(botEnabled)}
              disabled={botEnabled === null || botToggling}
              onChange={toggleBot}
              onClick={(e) => e.stopPropagation()}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: "#7c3aed" },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#7c3aed" },
              }}
            />
          </Box>
        </Tooltip>}
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          minHeight: 0,
        }}
      >
        {/* Left sidebar: single contact, same styling as main chat sidebar */}
        <Box
          sx={{
            width: 360,
            borderRight: 1,
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            ...WB_SX.card,
            borderRadius: 0,
            backgroundColor: WB_UI.color.cardBgStrong,
          }}
        >
          <Box sx={{ p: 1.5 }}>
            <TextField
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search contacts"
              size="small"
              fullWidth
            />
          </Box>
          <Divider />
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            <List disablePadding>
              <ListItem
                button
                selected
                sx={{
                  backgroundColor: WB_UI.color.softSuccessBg,
                  "&:hover": {
                    backgroundColor: WB_UI.color.softSuccessBg,
                  },
                  cursor: "default",
                  height: "72px",
                }}
              >
                <ListItemAvatar>
                  <Badge badgeContent={0} invisible>
                    <Avatar
                      sx={{
                        bgcolor: WB_UI.color.whatsappDark,
                        width: 48,
                        height: 48,
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                      >
                        {crmName || displayPhone}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {messages.length
                          ? new Date(
                              messages[messages.length - 1].created_at
                            ).toLocaleString("en-US", {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : ""}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: "0.75rem",
                      }}
                    >
                      {crmName ? displayPhone : "No name saved"}
                    </Typography>
                  }
                />
              </ListItem>
              <Divider />
            </List>
          </Box>
        </Box>

        {/* Right chat pane (same as main chat window) */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              backgroundColor: "#e5ddd5",
              backgroundImage: `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIAAAACACAYAAADDPmHLAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAAAdgAAAHYBTnsmCAAAABl0RVh0U29mdHdhcmUAd3d3Lmlua3NjYXBlLm9yZ5vuPBoAAAAnSURBVHic7cEBDQAAAMIg+6c2BL4mAAAAAAAAAAAAAAAAAAD4B7a4AAFj8b8zAAAAAElFTkSuQmCC")`,
            }}
            ref={messagesListRef}
            onScroll={handleMessagesScroll}
          >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : messagesWithDateHeaders.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "text.secondary",
              }}
            >
              <ChatIcon sx={{ fontSize: 64, mb: 2, opacity: 0.25, color: WB_UI.color.whatsappDark }} />
              <Typography variant="body1">
                No messages yet. Start a conversation!
              </Typography>
            </Box>
          ) : (
            messagesWithDateHeaders.map((message) => {
              if (message.__type === "date-header") {
                return (
                  <Box
                    key={message.id}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      my: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1.5,
                        backgroundColor: "rgba(0, 0, 0, 0.15)",
                        color: "white",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                      }}
                    >
                      {message.label}
                    </Box>
                  </Box>
                );
              }

              if (message.is_template && message.template_id) {
                const templateDetails = templateDetailsCache[message.template_id];

                if (templateDetails) {
                  return (
                    <Box
                      key={message.id}
                      sx={{
                        display: "flex",
                        justifyContent:
                          message.sender === "user" ? "flex-start" : "flex-end",
                        mb: 1,
                      }}
                    >
                      <TemplateMessageBubble
                        templateDetails={templateDetails}
                        payload={message.payload}
                        mediaLink={message.media_link}
                        mediaType={message.media_type}
                        sender={message.sender}
                        timestamp={message.timestamp}
                        status={message.status}
                        isBotReply={message.is_bot_reply}
                        senderName={message.sender_name}
                      />
                    </Box>
                  );
                }

                if (loadingTemplates[message.template_id]) {
                  return (
                    <Box
                      key={message.id}
                      sx={{
                        display: "flex",
                        justifyContent:
                          message.sender === "user" ? "flex-start" : "flex-end",
                        mb: 1,
                      }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          maxWidth: "70%",
                          backgroundColor:
                            message.sender === "user"
                              ? "background.paper"
                              : message.is_bot_reply
                              ? "#6B46C1"
                              : "#dcf8c6",
                          color:
                            message.sender === "user"
                              ? "text.primary"
                              : message.is_bot_reply
                              ? "rgba(255,255,255,0.95)"
                              : "text.primary",
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <CircularProgress size={16} sx={{ color: "inherit" }} />
                        <Typography variant="body2">
                          Loading template...
                        </Typography>
                      </Paper>
                    </Box>
                  );
                }

                return (
                  <Box
                    key={message.id}
                    sx={{
                      display: "flex",
                      justifyContent:
                        message.sender === "user" ? "flex-start" : "flex-end",
                      mb: 1,
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        maxWidth: "70%",
                        backgroundColor:
                          message.sender === "user"
                            ? "background.paper"
                            : message.is_bot_reply
                            ? "#6B46C1"
                            : "#dcf8c6",
                        color:
                          message.sender === "user"
                            ? "text.primary"
                            : message.is_bot_reply
                            ? "rgba(255,255,255,0.95)"
                            : "text.primary",
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="body1">
                        {message.text || "Template message (unavailable)"}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems:
                            message.sender === "user" ? "flex-start" : "flex-end",
                          mt: 0.5,
                          gap: 0.25,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              opacity: 0.7,
                            }}
                          >
                            {message.timestamp}
                          </Typography>
                          {message.sender === "bot" && (
                            <MessageStatusIcon status={message.status} />
                          )}
                        </Box>
                        {message.sender === "bot" && message.sender_name && (
                          <Typography
                            variant="caption"
                            sx={{
                              opacity: 0.85,
                              fontSize: "0.7rem",
                            }}
                          >
                            {message.sender_name}
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  </Box>
                );
              }

              const hasMedia =
                message.media_link &&
                message.media_type &&
                message.media_type !== "TEXT";
              const mediaUrl = message.media_link
                ? message.media_link.startsWith("http://") ||
                  message.media_link.startsWith("https://")
                  ? message.media_link
                  : `https://pub-b8f74165803246fbaee6ed60fe08c081.r2.dev/${
                      message.media_link.startsWith("/")
                        ? message.media_link.slice(1)
                        : message.media_link
                    }`
                : null;

              return (
                <Box
                  key={message.id}
                  sx={{
                    display: "flex",
                    justifyContent:
                      message.sender === "user" ? "flex-start" : "flex-end",
                    mb: 1,
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: hasMedia ? 0 : 1.5,
                      maxWidth: "70%",
                      backgroundColor:
                        message.sender === "user"
                          ? "background.paper"
                          : message.is_bot_reply
                          ? "#6B46C1"
                          : "#dcf8c6",
                      color:
                        message.sender === "user"
                          ? "text.primary"
                          : message.is_bot_reply
                          ? "rgba(255,255,255,0.95)"
                          : "black",
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    {hasMedia && mediaUrl && (
                      <Box sx={{ mb: message.text ? 1 : 0 }}>
                        {message.media_type === "IMAGE" && (
                          <>
                            <Box
                              component="img"
                              src={mediaUrl}
                              alt="Image"
                              onError={(e) => {
                                console.error("Image load error:", e);
                                e.target.style.display = "none";
                              }}
                              sx={{
                                width: "100%",
                                maxHeight: 300,
                                display: "block",
                                objectFit: "contain",
                                backgroundColor: "#f5f5f5",
                              }}
                            />
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                mt: 0.5,
                                px: 1.5,
                              }}
                            >
                              <IconButton
                                size="small"
                                component="a"
                                href={mediaUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                sx={{
                                  color:
                                    message.sender === "user"
                                      ? WB_UI.color.whatsappDark
                                      : message.is_bot_reply
                                      ? "rgba(255,255,255,0.9)"
                                      : WB_UI.color.whatsappDark,
                                }}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </>
                        )}
                        {message.media_type === "VIDEO" && (
                          <>
                            <Box
                              component="video"
                              src={mediaUrl}
                              controls
                              sx={{
                                width: "100%",
                                maxHeight: 300,
                                display: "block",
                                backgroundColor: "#000",
                              }}
                            />
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                mt: 0.5,
                                px: 1.5,
                              }}
                            >
                              <IconButton
                                size="small"
                                component="a"
                                href={mediaUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                sx={{
                                  color:
                                    message.sender === "user"
                                      ? WB_UI.color.whatsappDark
                                      : message.is_bot_reply
                                      ? "rgba(255,255,255,0.9)"
                                      : WB_UI.color.whatsappDark,
                                }}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </>
                        )}
                        {message.media_type === "DOCUMENT" && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              p: 1.5,
                              backgroundColor: "#f5f5f5",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <DescriptionIcon
                                sx={{ fontSize: 32, color: WB_UI.color.whatsappDark }}
                              />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  Document
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {message.media_link.split("/").pop() ||
                                    "Document"}
                                </Typography>
                              </Box>
                            </Box>
                            <IconButton
                              size="small"
                              component="a"
                              href={mediaUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                        {message.media_type === "AUDIO" && (
                          <Box
                            sx={{
                              p: 1.5,
                              backgroundColor: "#f5f5f5",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                            }}
                          >
                            <Typography variant="body2">Audio file</Typography>
                            <IconButton
                              size="small"
                              component="a"
                              href={mediaUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    )}

                    {message.text && (
                      <Box
                        sx={{
                          px: hasMedia ? 1.5 : 0,
                          pb: hasMedia ? 1 : 0,
                          pt: hasMedia ? 1 : 0,
                        }}
                      >
                        <Typography variant="body1">{message.text}</Typography>
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems:
                          message.sender === "user" ? "flex-start" : "flex-end",
                        px: 1.5,
                        pb: 1,
                        gap: 0.25,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            opacity: 0.7,
                          }}
                        >
                          {message.timestamp}
                        </Typography>
                        {message.sender === "bot" && (
                          <MessageStatusIcon status={message.status} />
                        )}
                      </Box>
                      {message.sender === "bot" && message.sender_name && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {message.is_bot_reply ? (
                            <SmartToyIcon sx={{ fontSize: 14, opacity: 0.85 }} />
                          ) : (
                            <PersonIcon sx={{ fontSize: 14, opacity: 0.85 }} />
                          )}
                          <Typography
                            variant="caption"
                            sx={{
                              opacity: 0.85,
                              fontSize: "0.7rem",
                            }}
                          >
                            Replied by {message.sender_name}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Box>
              );
            })
              )}
          </Box>

          <Box
            sx={{
              px: { xs: 1.5, md: 3 },
              pb: { xs: 1.5, md: 2 },
              pt: 1,
              borderTop: 1,
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Box
              sx={{
                maxWidth: 900,
                mx: "auto",
                display: "flex",
                gap: 1,
                alignItems: "flex-end",
              }}
            >
              <input
                type="file"
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={handleMediaFileChange}
              />
              <IconButton
                size="small"
                onClick={() =>
                  fileInputRef.current && fileInputRef.current.click()
                }
                sx={{ mr: 0.5, color: WB_UI.color.whatsappDark }}
              >
                <AttachFileIcon fontSize="small" />
              </IconButton>
              <TextField
                fullWidth
                multiline
                minRows={1}
                maxRows={4}
                placeholder={
                  mediaFile ? "Add a caption (optional)..." : "Type a message..."
                }
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyDown={handleKeyDown}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      size="small"
                      ref={emojiButtonRef}
                      onClick={() => setShowEmojiPicker((prev) => !prev)}
                      sx={{ mr: 0.5 }}
                    >
                      <EmojiEmotionsIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
              <Button
                variant="contained"
                 endIcon={<SendIcon />}
                 onClick={mediaFile ? handleMediaUpload : handleSend}
                 disabled={
                   (mediaFile ? uploadingMedia || sendingMedia : !messageInput.trim() || sending)
                 }
                sx={{ ...WB_SX.primaryButton, minWidth: 80 }}
              >
                {uploadingMedia || sendingMedia || sending ? "Sending..." : "Send"}
              </Button>
            </Box>
            {mediaFile && (
              <Box
                sx={{
                  maxWidth: 900,
                  mx: "auto",
                  mt: 1,
                  p: 1,
                  borderRadius: 1,
                  border: "1px solid rgba(0,0,0,0.12)",
                  backgroundColor: "background.default",
                  position: "relative",
                }}
              >
                <IconButton
                  size="small"
                  onClick={handleCancelMedia}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    backgroundColor: "error.main",
                    color: "white",
                    zIndex: 1,
                    "&:hover": {
                      backgroundColor: "error.dark",
                    },
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>

                {mediaType === "image" && mediaPreviewUrl && (
                  <Box
                    component="img"
                    src={mediaPreviewUrl}
                    alt="Preview"
                    sx={{
                      maxWidth: "100%",
                      maxHeight: 200,
                      borderRadius: 1,
                      display: "block",
                    }}
                  />
                )}

                {mediaType === "video" && mediaPreviewUrl && (
                  <Box
                    component="video"
                    src={mediaPreviewUrl}
                    controls
                    sx={{
                      maxWidth: "100%",
                      maxHeight: 200,
                      borderRadius: 1,
                      display: "block",
                    }}
                  />
                )}

                {(mediaType === "document" || mediaType === "audio") && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                    }}
                  >
                    {mediaType === "document" ? (
                      <InsertDriveFileIcon
                        sx={{ fontSize: 40, color: WB_UI.color.whatsappDark }}
                      />
                    ) : (
                      <DescriptionIconMui
                        sx={{ fontSize: 40, color: WB_UI.color.whatsappDark }}
                      />
                    )}
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {mediaFile.name}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            {showEmojiPicker && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 80,
                  right: 80,
                  zIndex: 1300,
                  p: 1,
                  backgroundColor: "background.paper",
                  borderRadius: 1,
                  boxShadow: 3,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(8, 1fr)",
                    gap: 0.5,
                  }}
                >
                  {professionalEmojis.map((emoji, index) => (
                    <IconButton
                      key={index}
                      onClick={() => handleEmojiClick(emoji)}
                      sx={{
                        fontSize: "24px",
                        width: "36px",
                        height: "36px",
                        padding: 0,
                      }}
                    >
                      {emoji}
                    </IconButton>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>

    {/* ── CRM dialogs: CRM-ADMIN / CRM-USER only ─────────────────────────── */}
    {hasCrmRole && (
    <>
    <Dialog open={addCrmOpen} onClose={() => { if (!savingCrm) { setAddCrmOpen(false); setCrmForm(EMPTY_CRM_FORM); } }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, fontSize: "0.95rem", pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 30, height: 30, borderRadius: "50%", backgroundColor: WB_UI.color.softInfoBg, border: `1px solid ${WB_UI.color.border}`, display: "grid", placeItems: "center" }}>
            <PersonAddOutlined sx={{ fontSize: 16, color: WB_UI.color.whatsappDark }} />
          </Box>
          {crmExists ? "Update CRM Contact" : "Add to CRM"}
        </Box>
        <IconButton size="small" onClick={() => setAddCrmOpen(false)} disabled={savingCrm}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5 }}>
        <Box sx={{ mb: 2, px: 1.5, py: 1, borderRadius: 1.5, backgroundColor: "#f9fafb", border: "1px solid #e5e7eb" }}>
          <Typography variant="caption" color="text.secondary">Phone number</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{displayPhone}</Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <TextField fullWidth label="Name" placeholder="e.g. Rahul Sharma" value={crmForm.name}
            onChange={(e) => setCrmForm((f) => ({ ...f, name: e.target.value }))} size="small" autoFocus
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          <TextField fullWidth label="Email" placeholder="e.g. rahul@example.com" value={crmForm.email}
            onChange={(e) => setCrmForm((f) => ({ ...f, email: e.target.value }))} size="small" type="email"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          <TextField fullWidth label="Company Name" placeholder="e.g. Acme Corp" value={crmForm.company_name}
            onChange={(e) => setCrmForm((f) => ({ ...f, company_name: e.target.value }))} size="small"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          <FormControl fullWidth size="small">
            <InputLabel>Source</InputLabel>
            <Select label="Source" value={crmForm.source}
              onChange={(e) => setCrmForm((f) => ({ ...f, source: e.target.value }))}
              sx={{ borderRadius: 2 }}>
              <MenuItem value=""><em>Select source</em></MenuItem>
              {SOURCE_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          {/* Status toggle */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: "block", fontWeight: 500 }}>Status</Typography>
            <Box sx={{ display: "flex", gap: 0.75 }}>
              {[{ value: "interested", label: "Interested" }, { value: "not_interested", label: "Not Interested" }].map(({ value: v, label }) => {
                const active = crmForm.status === v;
                return (
                  <Button key={v} size="small" onClick={() => setCrmForm((f) => ({ ...f, status: v }))}
                    sx={{
                      textTransform: "none", fontSize: "0.75rem", fontWeight: 600, borderRadius: 2, px: 1.5,
                      border: `1px solid ${active ? (v === "interested" ? "#bbf7d0" : "#fecaca") : "#e5e7eb"}`,
                      backgroundColor: active ? (v === "interested" ? "#f0fdf4" : "#fef2f2") : "transparent",
                      color: active ? (v === "interested" ? "#166534" : "#991b1b") : "text.secondary",
                      "&:hover": { backgroundColor: v === "interested" ? "#dcfce7" : "#fee2e2" },
                    }}
                  >{label}</Button>
                );
              })}
            </Box>
          </Box>
          {/* Bot toggle — only visible to WB-AGENT */}
          {isWbAgent && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, py: 1,
            borderRadius: 2, border: `1px solid ${crmForm.is_bot_enabled ? "#ddd6fe" : "#e5e7eb"}`,
            backgroundColor: crmForm.is_bot_enabled ? "#f5f3ff" : "#f9fafb" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SmartToyIcon sx={{ fontSize: 18, color: crmForm.is_bot_enabled ? "#7c3aed" : "#9ca3af" }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.8rem", color: crmForm.is_bot_enabled ? "#7c3aed" : "text.primary" }}>
                  Auto Bot Reply
                </Typography>
                <Typography variant="caption" color="text.secondary">Automatically reply using bot</Typography>
              </Box>
            </Box>
            <Switch size="small" checked={crmForm.is_bot_enabled}
              onChange={(e) => setCrmForm((f) => ({ ...f, is_bot_enabled: e.target.checked }))}
              sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#7c3aed" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#7c3aed" } }} />
          </Box>
          )}
        </Box>
        {crmExists && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
            This contact already exists in CRM. Only the fields you fill in will be updated.
          </Typography>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button size="small" onClick={() => { setAddCrmOpen(false); setCrmForm(EMPTY_CRM_FORM); }} disabled={savingCrm} sx={{ textTransform: "none", borderRadius: 2, border: "1px solid #e5e7eb", color: "text.secondary" }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleAddToCrm}
          disabled={savingCrm}
          sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, backgroundColor: "#1a2333", "&:hover": { backgroundColor: "#374151" } }}
        >
          {savingCrm ? "Saving…" : crmExists ? "Update Contact" : "Add to CRM"}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={editNameOpen} onClose={() => !savingCrm && setEditNameOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, fontSize: "0.95rem", pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 30, height: 30, borderRadius: "50%", backgroundColor: WB_UI.color.softInfoBg, border: `1px solid ${WB_UI.color.border}`, display: "grid", placeItems: "center" }}>
            <EditOutlined sx={{ fontSize: 16, color: "#111827" }} />
          </Box>
          Edit Contact Name
        </Box>
        <IconButton size="small" onClick={() => setEditNameOpen(false)} disabled={savingCrm}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5 }}>
        <Box sx={{ mb: 2, px: 1.5, py: 1, borderRadius: 1.5, backgroundColor: "#f9fafb", border: "1px solid #e5e7eb" }}>
          <Typography variant="caption" color="text.secondary">Phone number</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{displayPhone}</Typography>
        </Box>
        <TextField
          fullWidth
          required
          label="Contact Name"
          placeholder="e.g. Rahul Sharma"
          value={crmNameInput}
          onChange={(e) => setCrmNameInput(e.target.value)}
          size="small"
          autoFocus
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          onKeyDown={(e) => { if (e.key === "Enter" && crmNameInput.trim() && !savingCrm) handleEditName(); }}
        />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button size="small" onClick={() => setEditNameOpen(false)} disabled={savingCrm} sx={{ textTransform: "none", borderRadius: 2, border: "1px solid #e5e7eb", color: "text.secondary" }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleEditName}
          disabled={savingCrm || !crmNameInput.trim()}
          sx={{ ...WB_SX.primaryButton, borderRadius: 2 }}
        >
          {savingCrm ? "Saving…" : "Save Name"}
        </Button>
      </DialogActions>
    </Dialog>
    </>
    )}
    </>
  );
};

export default ContactChatWindow;

